import React, { useState } from 'react';
import { Card, Typography, Spin, Empty, Button, Tag, Space, Modal } from 'antd';
import { EyeOutlined, FileOutlined, FilePdfOutlined, FileWordOutlined, FileExcelOutlined, FileImageOutlined, FileZipOutlined, FolderOutlined } from '@ant-design/icons';
import { useClassSharedFiles } from '../hooks/classSharedFiles';
import { useStudentCheck } from '../hooks/classFeedback';
import { useResponsive } from '../hooks/useResponsive';
import { useSecurityProtection } from '../hooks/useSecurityProtection';
import authService from '../services/authService';
import { listObjects } from '../utils/fileApi';
import FilePreviewModal from '../components/FilePreviewModal';

const { Title, Text } = Typography;

const SharedFiles: React.FC = () => {
  const { isMobile } = useResponsive();
  const currentUser = authService.getCurrentUser();
  const userId = currentUser?.id || '';
  useSecurityProtection();

  const [isFolderModalOpen, setIsFolderModalOpen] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState<any>(null);
  const [folderFiles, setFolderFiles] = useState<any[]>([]);
  const [isLoadingFolderFiles, setIsLoadingFolderFiles] = useState(false);
  const [folderPath, setFolderPath] = useState<string[]>([]);
  const [previewFile, setPreviewFile] = useState<{ file_url: string; file_name: string } | null>(null);

  // Get student's class information
  const { data: studentData, isLoading: isStudentLoading } = useStudentCheck(userId);
  const classId = studentData?.id;

  // Get shared files for the class
  const { data: sharedFiles, isLoading: isFilesLoading, error } = useClassSharedFiles(classId || '');

  // Function to get file icon based on file type
  const getFileIcon = (fileType: string) => {
    const type = fileType.toLowerCase();
    if (type === 'folder') return <FolderOutlined style={{ fontSize: '24px', color: '#faad14' }} />;
    if (type.includes('pdf')) return <FilePdfOutlined style={{ fontSize: '24px', color: '#ff4d4f' }} />;
    if (type.includes('word') || type.includes('doc')) return <FileWordOutlined style={{ fontSize: '24px', color: '#1890ff' }} />;
    if (type.includes('excel') || type.includes('sheet')) return <FileExcelOutlined style={{ fontSize: '24px', color: '#52c41a' }} />;
    if (type.includes('image') || type.includes('png') || type.includes('jpg') || type.includes('jpeg')) return <FileImageOutlined style={{ fontSize: '24px', color: '#faad14' }} />;
    if (type.includes('zip') || type.includes('rar')) return <FileZipOutlined style={{ fontSize: '24px', color: '#722ed1' }} />;
    return <FileOutlined style={{ fontSize: '24px', color: '#666' }} />;
  };


  // Function to format file size
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  // Function to format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Function to load folder contents
  const loadFolderContents = async (path: string) => {
    setIsLoadingFolderFiles(true);
    setFolderFiles([]);

    try {
      const objects = await listObjects(path.endsWith('/') ? path : path + '/');

      // Process the objects - include both files and folders
      const processedFiles = objects.map((item: any) => ({
        id: Math.random(),
        key: item.key,
        file_name: item.isFolder ? item.key : (item.key.split('/').pop() || item.key),
        fullKey: item.fullKey,
        isFolder: item.isFolder,
        size: item.size || 0,
        lastModified: item.lastModified,
        storageClass: item.storageClass,
        file_type: item.isFolder ? 'folder' : getFileTypeFromName(item.key),
        file_size: item.size || 0,
        file_url: item.fullKey || '',
      }));

      // Sort: folders first, then files
      const sortedFiles = processedFiles.sort((a: any, b: any) => {
        if (a.isFolder && !b.isFolder) return -1;
        if (!a.isFolder && b.isFolder) return 1;
        return a.file_name.localeCompare(b.file_name);
      });

      setFolderFiles(sortedFiles);
    } catch (error) {
      console.error('Error fetching folder contents:', error);
      setFolderFiles([]);
    } finally {
      setIsLoadingFolderFiles(false);
    }
  };

  // Function to handle folder click (initial open)
  const handleFolderClick = async (folder: any) => {
    setSelectedFolder(folder);
    setIsFolderModalOpen(true);
    setFolderPath([folder.file_name]);

    const path = folder.file_url?.split('.com/')?.[1] || folder.file_name;
    await loadFolderContents(path);
  };

  // Function to navigate into a subfolder
  const handleSubfolderClick = async (subfolder: any) => {
    const newPath = [...folderPath, subfolder.file_name];
    setFolderPath(newPath);
    await loadFolderContents(subfolder.fullKey);
  };

  // Function to navigate back to parent folder
  const handleBackClick = async () => {
    if (folderPath.length <= 1) return;
    
    const newPath = folderPath.slice(0, -1);
    setFolderPath(newPath);
    
    // Reconstruct the parent folder path
    const parentPath = selectedFolder?.file_url?.split('.com/')?.[1]?.split('/').slice(0, -1).join('/') + '/' + 
      newPath.slice(1).join('/') + (newPath.length > 1 ? '/' : '');
    const basePath = selectedFolder?.file_url?.split('.com/')?.[1] || selectedFolder?.file_name;
    const targetPath = newPath.length === 1 ? basePath : parentPath;
    
    await loadFolderContents(targetPath);
  };

  // Helper function to get file type from file name
  const getFileTypeFromName = (fileName: string) => {
    const ext = fileName.split('.').pop()?.toLowerCase() || '';
    if (['pdf'].includes(ext)) return 'PDF';
    if (['doc', 'docx'].includes(ext)) return 'Word';
    if (['xls', 'xlsx'].includes(ext)) return 'Excel';
    if (['png', 'jpg', 'jpeg', 'gif'].includes(ext)) return 'Image';
    if (['zip', 'rar'].includes(ext)) return 'Archive';
    return ext.toUpperCase();
  };

  if (isStudentLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px' 
      }}>
        <Spin size="large" tip="Loading class information..." />
      </div>
    );
  }

  if (!classId) {
    return (
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: isMobile ? '16px' : '24px'
      }}>
        <Card>
          <Empty
            description="You are not enrolled in any class"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </Card>
      </div>
    );
  }

  if (isFilesLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px' 
      }}>
        <Spin size="large" tip="Loading shared files..." />
      </div>
    );
  }

  return (
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      padding: isMobile ? '16px' : '24px',
      background: '#f8f9fa',
      minHeight: '100vh'
    }}>
      <div style={{ marginBottom: isMobile ? '24px' : '32px' }}>
        <Title level={2} style={{ 
          margin: 0, 
          fontSize: isMobile ? '20px' : '28px',
          fontWeight: '600',
          marginBottom: '8px'
        }}>
          Shared Files
        </Title>
        {studentData?.class_title && (
          <Text type="secondary" style={{ fontSize: isMobile ? '14px' : '16px' }}>
            Class: {studentData.class_title}
          </Text>
        )}
      </div>

      {error ? (
        <Card>
          <Empty
            description="Failed to load shared files. Please try again later."
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </Card>
      ) : !sharedFiles || sharedFiles.length === 0 ? (
        <Card>
          <Empty
            description="No shared files available yet"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </Card>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(350px, 1fr))',
          gap: isMobile ? '16px' : '24px'
        }}>
          {sharedFiles.map((file) => (
            <Card
              key={file.id}
              hoverable
              style={{
                borderRadius: '12px',
                border: '1px solid #e8e8e8',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                transition: 'all 0.3s ease',
                cursor: file.file_type.toLowerCase() === 'folder' ? 'pointer' : 'default'
              }}
              bodyStyle={{ padding: isMobile ? '16px' : '20px' }}
              onClick={() => file.file_type.toLowerCase() === 'folder' && handleFolderClick(file)}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.12)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <div style={{ display: 'flex', gap: '16px' }}>
                <div style={{
                  width: '56px',
                  height: '56px',
                  background: '#f5f5f5',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  {getFileIcon(file.file_type)}
                </div>
                
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Title level={5} style={{ 
                    margin: '0 0 8px 0',
                    fontSize: isMobile ? '14px' : '16px',
                    fontWeight: '600',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {file.file_name}
                  </Title>
                  
                  <Space direction="vertical" size={4} style={{ width: '100%' }}>
                    <Text type="secondary" style={{ fontSize: isMobile ? '12px' : '13px' }}>
                      {formatFileSize(file.file_size)}
                    </Text>
                    
                    {file.description && (
                      <Text 
                        type="secondary" 
                        style={{ 
                          fontSize: isMobile ? '12px' : '13px',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}
                      >
                        {file.description}
                      </Text>
                    )}
                    
                    <Text type="secondary" style={{ fontSize: isMobile ? '11px' : '12px' }}>
                      Uploaded: {formatDate(file.created_at)}
                    </Text>
                  </Space>
                </div>
              </div>
              
              <div style={{ 
                marginTop: '16px',
                paddingTop: '16px',
                borderTop: '1px solid #f0f0f0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <Tag color={file.file_type.toLowerCase() === 'folder' ? 'gold' : 'blue'} style={{ margin: 0 }}>
                  {file.file_type}
                </Tag>
                
                {file.file_type.toLowerCase() === 'folder' ? (
                  <Space>
                    <Button
                      type="default"
                      icon={<FolderOutlined />}
                      size={isMobile ? 'small' : 'middle'}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleFolderClick(file);
                      }}
                      style={{
                        borderRadius: '8px',
                        borderColor: '#faad14',
                        color: '#faad14'
                      }}
                    >
                      Open
                    </Button>
                  </Space>
                ) : (
                  <Button
                    type="primary"
                    icon={<EyeOutlined />}
                    size={isMobile ? 'small' : 'middle'}
                    onClick={(e) => {
                      e.stopPropagation();
                      setPreviewFile({ file_url: file.file_url, file_name: file.file_name });
                    }}
                    style={{
                      borderRadius: '8px',
                      background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                      border: 'none'
                    }}
                  >
                    Preview
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      <FilePreviewModal
        key={previewFile?.file_url || 'closed'}
        file={previewFile}
        onClose={() => setPreviewFile(null)}
      />

      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <FolderOutlined style={{ color: '#faad14', fontSize: '20px' }} />
            {folderPath.map((name, index) => (
              <span key={index} style={{ display: 'flex', alignItems: 'center' }}>
                {index > 0 && <span style={{ margin: '0 4px', color: '#999' }}>/</span>}
                <span style={{ 
                  color: index === folderPath.length - 1 ? '#000' : '#1890ff',
                  cursor: index < folderPath.length - 1 ? 'pointer' : 'default'
                }}>
                  {name}
                </span>
              </span>
            ))}
            {folderPath.length > 1 && (
              <Button 
                size="small" 
                onClick={handleBackClick}
                style={{ marginLeft: '8px' }}
              >
                ← Back
              </Button>
            )}
          </div>
        }
        open={isFolderModalOpen}
        onCancel={() => {
          setIsFolderModalOpen(false);
          setSelectedFolder(null);
          setFolderFiles([]);
          setFolderPath([]);
        }}
        footer={null}
        width={isMobile ? '95%' : 800}
        style={{ top: 20 }}
      >
        {selectedFolder && (
          <div>
            {selectedFolder.description && (
              <div style={{ 
                marginBottom: '20px', 
                padding: '12px', 
                background: '#f5f5f5', 
                borderRadius: '8px' 
              }}>
                <Text type="secondary">{selectedFolder.description}</Text>
              </div>
            )}
            
            {isLoadingFolderFiles ? (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                minHeight: '200px' 
              }}>
                <Spin size="large" tip="Loading folder contents..." />
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(250px, 1fr))',
                gap: '16px',
                maxHeight: '60vh',
                overflowY: 'auto',
                overflowX: 'hidden',
                padding: '4px',
                scrollbarWidth: 'thin',
                scrollbarColor: '#888 #f1f1f1'
              }}>
                {folderFiles.length === 0 ? (
                  <Empty 
                    description="No files in this folder" 
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    style={{ gridColumn: '1 / -1', padding: '40px 0' }}
                  />
                ) : (
                  folderFiles.map((file) => (
                  <Card
                    key={file.id}
                    size="small"
                    hoverable
                    style={{
                      borderRadius: '8px',
                      border: '1px solid #e8e8e8',
                      cursor: file.isFolder ? 'pointer' : 'default'
                    }}
                    onClick={() => file.isFolder && handleSubfolderClick(file)}
                  >
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        background: file.isFolder ? '#fff7e6' : '#f5f5f5',
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        {getFileIcon(file.file_type)}
                      </div>
                      
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ 
                          fontSize: '13px',
                          fontWeight: '500',
                          marginBottom: '4px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {file.file_name}
                        </div>
                        
                        <Space direction="vertical" size={2} style={{ width: '100%' }}>
                          {!file.isFolder && (
                            <Text type="secondary" style={{ fontSize: '11px' }}>
                              {formatFileSize(file.file_size)}
                            </Text>
                          )}
                          <Tag color={file.isFolder ? 'gold' : 'blue'} style={{ fontSize: '10px', padding: '0 6px', margin: 0 }}>
                            {file.file_type}
                          </Tag>
                        </Space>
                      </div>
                    </div>
                    
                    {file.isFolder ? (
                      <Space direction="vertical" size={8} style={{ marginTop: '12px', width: '100%' }}>
                        <Button
                          type="default"
                          icon={<FolderOutlined />}
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSubfolderClick(file);
                          }}
                          style={{
                            width: '100%',
                            borderRadius: '6px',
                            borderColor: '#faad14',
                            color: '#faad14'
                          }}
                        >
                          Open Folder
                        </Button>
                      </Space>
                    ) : (
                      <Button
                        type="primary"
                        icon={<EyeOutlined />}
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPreviewFile({ file_url: file.file_url, file_name: file.file_name });
                        }}
                        style={{
                          marginTop: '12px',
                          width: '100%',
                          borderRadius: '6px',
                          background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                          border: 'none'
                        }}
                      >
                        Preview
                      </Button>
                    )}
                  </Card>
                  ))
                )}
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};
export default SharedFiles;
